import toast from 'react-hot-toast'
import { api } from '../lib/api'
import { supabase } from '../lib/supabase'

const PENDING_NOTIFICATION_ROUTE_KEY = 'optioneer_pending_notification_route'

export type NotificationRouteData = {
  route?: string | null
  event_id?: string | null
  alert_type?: string | null
  position_id?: string | null
  strategy_id?: string | null
  trade_id?: string | null
}

function normalizeNotificationData(data: unknown): NotificationRouteData {
  if (!data || typeof data !== 'object') return {}
  const record = data as Record<string, unknown>
  return {
    route: typeof record.route === 'string' ? record.route : null,
    event_id: typeof record.event_id === 'string' ? record.event_id : null,
    alert_type: typeof record.alert_type === 'string' ? record.alert_type : null,
    position_id: typeof record.position_id === 'string' ? record.position_id : null,
    strategy_id: typeof record.strategy_id === 'string' ? record.strategy_id : null,
    trade_id: typeof record.trade_id === 'string' ? record.trade_id : null,
  }
}

function resolveRoute(data: NotificationRouteData): string {
  if (data.route?.startsWith('/')) return data.route
  if (data.position_id) return `/portfolio?position_id=${encodeURIComponent(data.position_id)}`
  if (data.strategy_id) return `/portfolio?strategy_id=${encodeURIComponent(data.strategy_id)}`
  return '/settings?tab=alerts'
}

function navigateToPath(path: string) {
  window.history.pushState(window.history.state, '', path)
  window.dispatchEvent(new Event('popstate'))
}

async function routeTargetExists(data: NotificationRouteData): Promise<boolean> {
  if (!data.position_id) return true

  try {
    await api.positions.getById(data.position_id, false)
    return true
  } catch {
    return false
  }
}

export async function handleNotificationRoute(rawData: unknown) {
  const data = normalizeNotificationData(rawData)
  const route = resolveRoute(data)

  const { data: sessionData } = await supabase.auth.getSession()
  if (!sessionData.session) {
    window.localStorage.setItem(
      PENDING_NOTIFICATION_ROUTE_KEY,
      JSON.stringify({ route, data, createdAt: Date.now() })
    )
    navigateToPath(`/signin?returnTo=${encodeURIComponent(route)}`)
    return
  }

  if (data.event_id) {
    void api.notifications.markRead(data.event_id).catch(() => undefined)
  }

  if (!(await routeTargetExists(data))) {
    toast('That alert target is no longer available.')
    navigateToPath('/settings?tab=alerts&notificationTargetMissing=1')
    return
  }

  navigateToPath(route)
}

export async function continuePendingNotificationRoute() {
  const raw = window.localStorage.getItem(PENDING_NOTIFICATION_ROUTE_KEY)
  if (!raw) return false

  window.localStorage.removeItem(PENDING_NOTIFICATION_ROUTE_KEY)
  try {
    const parsed = JSON.parse(raw) as { route?: string; data?: NotificationRouteData }
    if (parsed.data) {
      await handleNotificationRoute(parsed.data)
      return true
    }
    if (parsed.route?.startsWith('/')) {
      navigateToPath(parsed.route)
      return true
    }
  } catch {
    // Ignore malformed local state.
  }

  return false
}
